import dayjs from "dayjs";

/**
 * Sequence Audit Trail — forensic log of every document-number event on the till.
 *
 * Production tills occasionally show gaps in bill sequences that cannot be
 * reproduced. Every mechanism that consumes, skips, or jumps the sequence
 * counter records an event here so support can answer "what happened to
 * bill number N" without guessing.
 *
 * Storage:
 *  - Primary: db.sequenceAuditLog (IndexedDB, added in DexieAdapter v6) —
 *    survives everything a localStorage-only trail didn't: ErrorBoundary
 *    resets, appVersionGate's version-bump cleanup, "Clear Cache" buttons,
 *    and any other `localStorage.clear()`/`removeItem` call elsewhere in the
 *    app — none of those touch IndexedDB tables. Pruned by age, not a small
 *    fixed count, since IndexedDB has orders of magnitude more headroom than
 *    localStorage's ~5-10MB quota.
 *  - Secondary: localStorage ring buffer (`seqAuditTrail`, capped) — kept only
 *    for the synchronous DevTools helper `window.__seqAuditSync()`. This copy
 *    is NOT durable (see above) and must not be treated as the source of truth.
 *
 *  NOTE: an earlier version of this file also wrote to db.logInformation as a
 *  "secondary" store. That table is a fire-and-forget upload queue — both
 *  Dashboard2/index.js and useOrderProcessing.js's log-sync tick read
 *  db.logInformation.toArray() and unconditionally delete every row they see
 *  within seconds, regardless of type or whether the upload succeeded. Events
 *  written there were being deleted almost immediately, which is why it kept
 *  showing up empty in forensic exports — it was never actually durable.
 *
 * Event types:
 *  - "consume"               — a bill number was assigned and the order saved locally
 *  - "cancel-consume"        — a cancelled-order number (CAN/) was assigned
 *  - "init"                  — counter initialized at login/till-registration
 *                              (Math.max(serverSeq, localMax) — jump point #1)
 *  - "di31-recovery"         — duplicate documentno detected, counter jumped to
 *                              local max + 1 (jump point #2)
 *  - "stale-identity-recovery" — DI-33 regenerated a sOrderID to avoid overwriting
 *                              a different existing order
 *  - "preview-shown"         — a documentno was generated for on-screen display on
 *                              a fresh cart, before any customer/payment step. If a
 *                              cart is abandoned before reaching those steps, this
 *                              is the ONLY record that number was ever issued.
 *
 * Never throws — billing must never fail because audit logging failed.
 */

const STORAGE_KEY = "seqAuditTrail";
const MAX_LOCAL_ENTRIES = 400;
const PRUNE_MAX_AGE_DAYS = 45;
const PRUNE_SAMPLE_RATE = 0.02; // ~1 in 50 writes triggers a prune check — pruning is O(n) so it shouldn't run on every write

export function logSequenceEvent(event, details = {}, dbAdapter = null) {
  const entry = { ts: dayjs().format("YYYY-MM-DD HH:mm:ss"), event, ...details };

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const trail = raw ? JSON.parse(raw) : [];
    trail.push(entry);
    while (trail.length > MAX_LOCAL_ENTRIES) trail.shift();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trail));
  } catch (err) {
    console.warn("[SEQ-AUDIT] localStorage mirror write failed:", err?.message);
  }

  try {
    if (dbAdapter?.sequenceAuditLog?.add) {
      dbAdapter.sequenceAuditLog.add(entry);
      if (Math.random() < PRUNE_SAMPLE_RATE) pruneOldEntries(dbAdapter);
    }
  } catch (err) {
    console.warn("[SEQ-AUDIT] sequenceAuditLog write failed:", err?.message);
  }
}

async function pruneOldEntries(dbAdapter) {
  try {
    const cutoff = dayjs().subtract(PRUNE_MAX_AGE_DAYS, "day").format("YYYY-MM-DD HH:mm:ss");
    const all = await dbAdapter.sequenceAuditLog.toArray();
    const staleIds = all.filter((e) => e.ts && e.ts < cutoff).map((e) => e.id);
    for (const id of staleIds) {
      await dbAdapter.sequenceAuditLog.delete(id);
    }
  } catch (err) {
    console.warn("[SEQ-AUDIT] Prune failed (non-blocking):", err?.message);
  }
}

/** Synchronous, localStorage-only snapshot — recent activity, not authoritative. Run: window.__seqAuditSync() */
export function getLocalSequenceAuditTrail() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Full durable trail from IndexedDB — the authoritative source. Run: await window.__seqAudit() */
export async function getSequenceAuditTrail(dbAdapter) {
  try {
    if (dbAdapter?.sequenceAuditLog?.toArray) {
      const rows = await dbAdapter.sequenceAuditLog.toArray();
      return rows.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
    }
  } catch (err) {
    console.warn("[SEQ-AUDIT] Reading sequenceAuditLog failed, falling back to localStorage:", err?.message);
  }
  return getLocalSequenceAuditTrail();
}

/**
 * Reconciles the ring-buffer event log against the durable per-sale ledger
 * (db.paymentsData[].orderPayments, added under DI-09 for audit traceability).
 * Pure function — no IO — so it stays testable without mocking Dexie/Zustand.
 *
 * The ring buffer only ever records *events*; it cannot show *state*, so it
 * is structurally blind to two failure classes:
 *  - identity-collision overwrites: db.orders is keyed on sOrderID, so a
 *    reused sOrderID silently overwrites the earlier order's row — nothing
 *    left to log an event against. orderPayments is append-only (++id pk),
 *    so a sOrderID with 2+ distinct documentnos there proves a collision even
 *    though only one survives in db.orders.
 *  - logging blackouts: a "consume" write can silently fail (localStorage
 *    quota, tab state, etc.) while the order itself saved fine — the
 *    documentno exists in orderPayments/db.orders with no matching event.
 */
export function reconcileOrderLedger({ ledgerEntries = [], orders = [], auditTrail = [], prefix = null }) {
  const bySOrderID = new Map();
  for (const entry of ledgerEntries) {
    if (!entry?.sOrderID) continue;
    if (!bySOrderID.has(entry.sOrderID)) bySOrderID.set(entry.sOrderID, []);
    bySOrderID.get(entry.sOrderID).push(entry);
  }

  const ordersBySOrderID = new Map(orders.map((o) => [o.sOrderID, o]));
  const overwrittenOrders = [];
  for (const [sOrderID, entries] of bySOrderID) {
    const distinctDocs = new Set(entries.map((e) => e.documentno));
    if (distinctDocs.size < 2) continue;
    const survivor = ordersBySOrderID.get(sOrderID);
    for (const entry of entries) {
      if (!survivor || entry.documentno !== survivor.documentno) {
        overwrittenOrders.push({
          sOrderID,
          documentno: entry.documentno,
          orderTotal: entry.orderTotal,
          timestamp: entry.timestamp,
          survivingDocumentno: survivor?.documentno || null,
        });
      }
    }
  }

  const consumedDocs = new Set(auditTrail.filter((e) => e.event === "consume").map((e) => e.documentno));
  const knownDocs = new Set([...ledgerEntries.map((e) => e.documentno), ...orders.map((o) => o.documentno)].filter(Boolean));
  const loggingGaps = prefix ? [...knownDocs].filter((d) => d.startsWith(prefix) && !consumedDocs.has(d)).sort() : [];

  return { overwrittenOrders, loggingGaps };
}

/**
 * One-command diagnostic for support: current counters, the full durable audit
 * trail, any orders still sitting on the till that never reached the server,
 * and a reconciliation against the durable order ledger (see reconcileOrderLedger)
 * so identity-collision overwrites and audit-logging blackouts surface without
 * needing a separate full IndexedDB export.
 * Run in DevTools console:  await window.__seqDiag()
 */
export async function exportSequenceDiagnostics() {
  const result = { generatedAt: dayjs().format("YYYY-MM-DD HH:mm:ss") };

  try {
    result.build = typeof window !== "undefined" && window.__CW_BUILD__ ? window.__CW_BUILD__ : null;
  } catch {
    result.build = null;
  }

  let prefix = null;
  let db = null;
  try {
    const { default: useSessionStore } = await import("../stores/useSessionStore");
    const { default: useAuthStore } = await import("../stores/useAuthStore");
    const s = useSessionStore.getState();
    prefix = useAuthStore.getState().tillData?.tillAccess?.cwrTill?.prefix || null;
    result.counters = {
      documentSequence: s.documentSequence,
      preorderSequence: s.preorderSequence,
      cancelledOrderSequence: s.cancelledOrderSequence,
      tillSessionId: s.tillSession?.tillSessionId || null,
      prefix,
    };
  } catch (err) {
    result.counters = { error: err?.message };
  }

  let allOrders = [];
  try {
    const { default: dbModule } = await import("../database");
    db = dbModule;
    allOrders = await db.orders.toArray();
    result.unsyncedOrders = allOrders
      .filter((o) => o.isSynced !== 1 && o.isSynced !== "1")
      .map((o) => ({
        documentno: o.documentno,
        sOrderID: o.sOrderID,
        isSynced: o.isSynced,
        syncAttempts: o.syncAttempts,
        orderTime: o.orderTime,
        lastSyncAttempt: o.lastSyncAttempt || null,
      }));
    result.localOrderCount = allOrders.length;
  } catch (err) {
    result.unsyncedOrders = { error: err?.message };
  }

  result.auditTrail = await getSequenceAuditTrail(db);

  try {
    const { default: dbModule } = await import("../database");
    db = db || dbModule;
    const paymentsRows = await db.paymentsData.toArray();
    const ledgerEntries = [];
    for (const row of paymentsRows) {
      for (const entry of row.orderPayments || []) {
        if (entry?.sOrderID) ledgerEntries.push(entry);
      }
    }
    const { overwrittenOrders, loggingGaps } = reconcileOrderLedger({
      ledgerEntries,
      orders: allOrders,
      auditTrail: result.auditTrail,
      prefix,
    });
    result.overwrittenOrders = overwrittenOrders;
    result.loggingGaps = loggingGaps;
  } catch (err) {
    result.overwrittenOrders = { error: err?.message };
    result.loggingGaps = { error: err?.message };
  }

  return result;
}

/**
 * Same data as __seqDiag(), but saves it straight to a file instead of
 * printing to console — avoids truncated/mangled copy-paste when handing
 * this off for support. Run in DevTools console: await window.__seqDiagDownload()
 */
export async function downloadSequenceDiagnostics() {
  const result = await exportSequenceDiagnostics();
  const prefix = (result.counters?.prefix || "till").replace(/[^a-zA-Z0-9]/g, "");
  const stamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
  const filename = `seqDiag_${prefix}_${stamp}.json`;

  const blob = new Blob([JSON.stringify(result, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return filename;
}

if (typeof window !== "undefined") {
  window.__seqAuditSync = getLocalSequenceAuditTrail;
  window.__seqAudit = async () => {
    const { default: db } = await import("../database");
    return getSequenceAuditTrail(db);
  };
  window.__seqDiag = exportSequenceDiagnostics;
  window.__seqDiagDownload = downloadSequenceDiagnostics;
}
